import './MigrationWaves.scss';

import { ApplicationGroupingService, MigrationWavesService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationGroup, MigrationPeriod, MigrationWave, StartDayOption } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Button, Container, Header, RadioGroup, Select, SelectProps, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { downloadFileFromUrl } from '../../../common/DownloadService';
import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { format } from '../../../common/util';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import AutoCreateWaves from './AutoCreateWaves';
import { ADD_WAVE_OPTIONS, AddWaveTypes, WAVE_OR_GROUP_TABS, WAVE_SORTING_OPTIONS,WaveSortingOptions } from './constants';
import CreateOrEditWave from './CreateOrEditWave';
import GroupsPanel from './GroupsPanel';
import GroupsTab from './GroupsTab';
import { AutoCreateWaveState, CreateOrEditWaveState, DeleteWaveState, RemoveFromWaveState } from './interface';
import MigrationWaveReport from './MigrationWaveReport';
import MigrationWavesTimeline from './MigrationWavesTimeline';

type Props = RouteComponentProps<MatchParam> & FlashContextInterface;
const migrationWavesService = new MigrationWavesService();
const applicationGroupingService = new ApplicationGroupingService();

function MigrationWaves({match, flash}: Props): JSX.Element {
    const portfolioId = match.params.portfolioId;
    const { alert } = flash;
    const [ readLoading, setReadLoading ] = useState(false); // set to true when waves and groups are loading
    const [ writeLoading, setWriteLoading ] = useState(false); // set to true when updating waves and groups, to enable/disable modal buttons
    const [ migrationWaves, setMigrationWaves ] = useState<MigrationWave[]>([]);
    const [ applicationGroups, setApplicationGroups ] = useState<ApplicationGroup[]>([]);
    const [ addWaveType, setAddWaveType ] = useState(ADD_WAVE_OPTIONS[0].value);
    const [ sortOrder, setSortOrder ] = useState<SelectProps.Option>(WAVE_SORTING_OPTIONS[0]);
    const { panelHandler } = useContext(HelpPanelContext);

    const [ deleteWaveDetails, setDeleteWaveDetails ] = useState<DeleteWaveState>({
        visible: false,
        waveId: '',
        waveName: '',
    });
    const [ removeFromWaveDetails, setRemoveFromWaveDetails ] = useState<RemoveFromWaveState>({
        visible: false,
        groupId: '',
        groupName: '',
        waveId: '',
        waveName: '',
    });
    const [ editWaveDetails, setEditWaveDetails ] = useState<CreateOrEditWaveState>({
        visible: false,
        waveToEdit: undefined,
        waveToSave: {},
        isInvalid: false,
    });
    const [ createWaveDetails, setCreateWaveDetails ] = useState<CreateOrEditWaveState>({
        visible: false,
        waveToSave: {},
        isInvalid: false,
    });
    const [ autoCreateDetails, setAutoCreateDetails ] = useState<AutoCreateWaveState>({
        isInvalid: false,
        previewLoading: false,
        previewWaves: [],
        previewErrMsg: '',
        migrationPeriodToSave: {
            startDayOption: StartDayOption.START_ON_MONDAY,
        },
    });
    const [ downloadLoading, setDownloadLoading ] = useState(false);

    const getMigrationWaves = useCallback(async () => {
        try {
            const waves = await migrationWavesService.list(portfolioId);
            waves.sort(sortByStartDateAsc);
            setMigrationWaves(waves);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_LOAD_WAVES);
        }
    }, [ alert, portfolioId ]);
    
    const getApplicationGroups = useCallback(async () => {
        try {
            const groups = await applicationGroupingService.list(portfolioId);
            setApplicationGroups(groups);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_GROUPS);
        }
    },[ alert, portfolioId ]);

    const loadGroupsAndWaves = useCallback(async () => {
        setReadLoading(true);
        await getMigrationWaves();
        await getApplicationGroups();
        setReadLoading(false);
    }, [ getApplicationGroups, getMigrationWaves ]);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.WAVE_PLANNING);
    }, [ panelHandler ]);

    useEffect(() => {
        loadGroupsAndWaves();
    }, [ loadGroupsAndWaves ]);

    useEffect(() => {
        const sortByField = sortOrder.value || '';
        const sortFunction = (a: MigrationWave, b: MigrationWave): number => {
            switch (sortByField) {
                case WaveSortingOptions.START_DATE_ASC:
                    return sortByStartDateAsc(a, b);
                case WaveSortingOptions.START_DATE_DESC:
                    return -sortByStartDateAsc(a, b);
                case WaveSortingOptions.END_DATE_ASC:
                    return sortByEndDateAsc(a, b);
                case WaveSortingOptions.END_DATE_DESC:
                    return -sortByEndDateAsc(a, b);
                case WaveSortingOptions.NAME_ASC:
                    return sortByNameAsc(a, b);
                case WaveSortingOptions.NAME_DESC:
                    return -sortByNameAsc(a, b);
                case WaveSortingOptions.OPEN_FIRST:
                    return sortByOpenFirst(a);
                case WaveSortingOptions.CLOSED_FIRST:
                    return -sortByOpenFirst(a);
                case WaveSortingOptions.TOTAL_SERVERS_ASC:
                    return sortByTotalServersAsc(a, b);
                case WaveSortingOptions.TOTAL_SERVERS_DESC:
                    return -sortByTotalServersAsc(a, b);
                case WaveSortingOptions.TOTAL_DATABASES_ASC:
                    return sortByTotalDBsAsc(a, b);
                case WaveSortingOptions.TOTAL_DATABASES_DESC:
                    return -sortByTotalDBsAsc(a, b);
                default:
                    return 1;
            }
        };
        setMigrationWaves(waves => {
            const wavesCopy = [...waves];
            wavesCopy.sort(sortFunction);
            return wavesCopy;
        });
    }, [ sortOrder ]);

    const removeGroupsFromWave = async (groupIds: string[]) => {
        try {
            await migrationWavesService.removeGroups(portfolioId, groupIds);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_REMOVE_GROUPS_FROM_WAVE);
        }
        loadGroupsAndWaves();
    };

    const addGroupsToWave = async (groupIds: string[], waveId: string) => {
        try {
            await migrationWavesService.addGroups(portfolioId, waveId, groupIds);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_ADD_GROUPS_TO_WAVE);
        }
        loadGroupsAndWaves();
    };

    const sortByStartDateAsc = (a: MigrationWave, b: MigrationWave): number => a.startDate < b.startDate ? -1 : 1;
    const sortByEndDateAsc = (a: MigrationWave, b: MigrationWave): number => a.endDate < b.endDate ? -1 : 1;
    const sortByNameAsc = (a: MigrationWave, b: MigrationWave): number => a.name < b.name ? -1 : 1;
    const sortByOpenFirst = (a: MigrationWave): number => a.closed ? 1 : -1;
    const sortByTotalServersAsc = (a: MigrationWave, b: MigrationWave): number => a.totalServers - b.totalServers;
    const sortByTotalDBsAsc = (a: MigrationWave, b: MigrationWave): number => a.totalDatabases - b.totalDatabases;

    const waveReports = migrationWaves.map(wave => {
        return (
            <MigrationWaveReport
                key={wave.migrationWaveId}
                wave={wave}
                setDeleteWaveDetails={setDeleteWaveDetails}
                setRemoveFromWaveDetails={setRemoveFromWaveDetails}
                setEditWaveDetails={setEditWaveDetails}>
            </MigrationWaveReport>
        );
    });
    
    const [ activeTabId, setActiveTabId ] = useState(WAVE_OR_GROUP_TABS[0].id);
    const onConfirmDeleteWave = async () => {
        setWriteLoading(true);
        try {
            await migrationWavesService.delete(portfolioId, deleteWaveDetails.waveId);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_DELETE_WAVE);
        }
        setWriteLoading(false);
        setDeleteWaveDetails({visible: false, waveId: '', waveName: ''});

        loadGroupsAndWaves();
    };
    const onConfirmRemoveFromWave = async () => {
        setWriteLoading(true);
        await removeGroupsFromWave([removeFromWaveDetails.groupId]);
        setWriteLoading(false);
        setRemoveFromWaveDetails({visible: false, groupId: '', groupName: '', waveName: '', waveId: ''});

        loadGroupsAndWaves();
    };
    const onConfirmEditWave = async () => {
        if (!editWaveDetails.waveToSave) {
            return;
        }
        setWriteLoading(true);
        try {
            await migrationWavesService.update(Object.assign({}, editWaveDetails.waveToEdit, editWaveDetails.waveToSave));
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_UPDATE_WAVE);
        }
        setWriteLoading(false);
        setEditWaveDetails({...editWaveDetails, visible: false, isInvalid: false});

        loadGroupsAndWaves();
    };

    const confirmCreate = async () => {
        if (addWaveType === AddWaveTypes.ADD_A_WAVE) {
            await onConfirmCreateWave();
        } else {
            await onConfirmAutoCreateWaves();
        }
    };
    const onConfirmCreateWave = async () => {
        if (!createWaveDetails.waveToSave) {
            return;
        }
        setWriteLoading(true);
        try {
            await migrationWavesService.create(portfolioId, createWaveDetails.waveToSave);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_CREATE_WAVE);
        }
        setWriteLoading(false);
        setCreateWaveDetails({...createWaveDetails, visible: false, isInvalid: false});

        loadGroupsAndWaves();
    };

    const onConfirmAutoCreateWaves = async () => {
        if (!autoCreateDetails.migrationPeriodToSave) {
            return;
        }
        setWriteLoading(true);
        try {
            await migrationWavesService.autoCreateWaves(
                portfolioId,
                autoCreateDetails.migrationPeriodToSave as MigrationPeriod,
                autoCreateDetails.migrationPeriodToSave.deleteExistingWaves,
            );
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.CANNOT_CREATE_WAVE);
        }
        setWriteLoading(false);
        setCreateWaveDetails({...createWaveDetails, visible: false});
        setAutoCreateDetails({
            isInvalid: false,
            previewLoading: false,
            previewWaves: [],
            previewErrMsg: ''
        });
        loadGroupsAndWaves();
    };
    const createDisabled = addWaveType === AddWaveTypes.ADD_A_WAVE
        ? createWaveDetails.isInvalid
        : autoCreateDetails.isInvalid || !autoCreateDetails.previewWaves?.length;
    
    
    async function downloadWaveReport() {
        setDownloadLoading(true);
        try {
            const url = await migrationWavesService.download(portfolioId);
            downloadFileFromUrl(url);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_DOWNLOAD_WAVES);
        }
        setDownloadLoading(false);
    }

    const openAddWavesDialog = () => {
        setCreateWaveDetails({...createWaveDetails, visible: true});
    };
    
    return (
        <SpaceBetween size='s'>
            <Header actions={
                <SpaceBetween direction='horizontal' size='m'>
                    <Button iconName='add-plus' onClick={openAddWavesDialog}>{constants.ADD_WAVES}</Button>
                    <Button iconName='download' onClick={downloadWaveReport} loading={downloadLoading} 
                        id='btn-download-wave' 
                        disabled={migrationWaves.length === 0}
                    >
                        {constants.DOWNLOAD_WAVE_BUTTON}
                    </Button>
                </SpaceBetween>
            }></Header>
            <Container>
                <SpaceBetween size='l'>
                    <MigrationWavesTimeline
                        migrationWaves={migrationWaves}
                        loading={readLoading}
                        addToWave={addGroupsToWave}
                        removeFromWave={removeGroupsFromWave}
                        openAddWavesDialog={openAddWavesDialog}>
                    </MigrationWavesTimeline>
                    <GroupsPanel groups={applicationGroups} loading={readLoading}></GroupsPanel>
                </SpaceBetween>
            </Container>
            <Tabs tabs={WAVE_OR_GROUP_TABS} activeTabId={activeTabId} onChange={event => setActiveTabId(event.detail.activeTabId)}></Tabs>
            { activeTabId === 'waves' ? (
                <Container header={
                    <Header
                        actions={<Select onChange={e => setSortOrder(e.detail.selectedOption)} selectedOption={sortOrder} options={WAVE_SORTING_OPTIONS} />}
                        counter={`(${migrationWaves.length})`}>{constants.WAVES}
                    </Header>}>
                    { readLoading ? <Spinner size='big'/> : <SpaceBetween size='l'>{waveReports}</SpaceBetween> }
                </Container>
            ) : null }
            { activeTabId === 'groups' ? (readLoading ? <Spinner size='big'/> :
                <GroupsTab groups={applicationGroups} waves={migrationWaves} addToWave={addGroupsToWave} removeFromWave={removeGroupsFromWave}></GroupsTab>
            ): null }
            <ConfirmationModal
                visible={deleteWaveDetails.visible}
                header={`${constants.DELETE_BUTTON_TEXT} ${deleteWaveDetails.waveName}`}
                onConfirm={onConfirmDeleteWave}
                onDismiss={() => setDeleteWaveDetails({...deleteWaveDetails, visible: false})}
                confirmLoading={writeLoading}>
                <Alert header={constants.THIS_ACTION_CANNOT_BE_UNDONE}>
                    {constants.YOU_ARE_ABOUT_TO_DELETE} {deleteWaveDetails.waveName}. {constants.FORM_DELETE_ERROR_PROCEED}</Alert>
            </ConfirmationModal>
            <ConfirmationModal
                visible={removeFromWaveDetails.visible}
                header={constants.REMOVE_GROUP_FROM_WAVE}
                onConfirm={onConfirmRemoveFromWave}
                onDismiss={() => setRemoveFromWaveDetails({...removeFromWaveDetails, visible: false})}
                confirmLoading={writeLoading}>
                <Alert header={constants.THIS_ACTION_CANNOT_BE_UNDONE}>
                    {format(constants.YOU_ARE_ABOUT_TO_REMOVE_GROUP_FROM_WAVE, [removeFromWaveDetails.groupName, removeFromWaveDetails.waveName])}
                    . {constants.FORM_DELETE_ERROR_PROCEED}
                </Alert>
            </ConfirmationModal>
            { editWaveDetails.visible ? (
                <ConfirmationModal
                    key='edit'
                    visible
                    onConfirm={onConfirmEditWave}
                    onDismiss={() => setEditWaveDetails({...editWaveDetails, visible: false})}
                    header={constants.EDIT_WAVE}
                    confirmDisabled={editWaveDetails.isInvalid}
                    confirmLoading={writeLoading}
                >
                    <CreateOrEditWave
                        createOrEditWaveDetails={editWaveDetails}
                        setCreateOrEditWaveDetails={setEditWaveDetails}
                    ></CreateOrEditWave>
                </ConfirmationModal>
            ) : null }
            { createWaveDetails.visible ? (
                <ConfirmationModal
                    key='create'
                    visible
                    onConfirm={confirmCreate}
                    onDismiss={() => setCreateWaveDetails({...createWaveDetails, visible: false})}
                    header={constants.CREATE_WAVE}
                    confirmDisabled={createDisabled}
                    confirmLoading={writeLoading}
                >
                    <h3>{constants.CHOOSE_ADD_WAVE_TYPE}</h3>
                    <RadioGroup value={addWaveType} items={ADD_WAVE_OPTIONS} onChange={e => setAddWaveType(e.detail.value)}></RadioGroup>
                    { addWaveType === AddWaveTypes.ADD_A_WAVE ? 
                        <CreateOrEditWave createOrEditWaveDetails={createWaveDetails} setCreateOrEditWaveDetails={setCreateWaveDetails} /> :
                        <AutoCreateWaves autoCreateDetails={autoCreateDetails} setAutoCreateDetails={setAutoCreateDetails} portfolioId={portfolioId} />
                    }
                </ConfirmationModal>
            ) : null }
        </SpaceBetween>
    );
}

export default withFlashContext(MigrationWaves);
